import type { MenuOptions } from "@circleco/compass/components/Menu";
import copyToClipboard from "copy-to-clipboard";

import { t } from "@/i18n-js/instance";
import { useBookmarkComment } from "@/react/components/CommentsCompass/data/useBookmarkComment";
import {
  isFlagged,
  isTopLevelComment,
} from "@/react/components/CommentsCompass/helpers";
import { useDeleteModal } from "@/react/components/CommentsCompass/Modals/DeleteModal";
import { useEditModal } from "@/react/components/CommentsCompass/Modals/EditModal";
import { useReportModal } from "@/react/components/CommentsCompass/Modals/ReportModal";
import { useCommentsConfig } from "@/react/components/CommentsCompass/Provider/ConfigContext";
import { useCommentsDeepLink } from "@/react/components/CommentsCompass/Provider/useCommentsDeepLink";
import { useCommentPermissions } from "@/react/components/CommentsCompass/Thread/Comment/useCommentPermissions";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { isTruthy } from "@/react/helpers/typeGuards";
import type { Comment } from "@/react/types/Comment";

export const useActionMenuOptions = (comment: Comment): MenuOptions => {
  const { spaceId, isReadOnly, onCommentDeleted } = useCommentsConfig();
  const { getCommentUrl, deepLinkedCommentId, showAllComments } =
    useCommentsDeepLink();
  const { canReport, canEdit, canDestroy, canBookmark } =
    useCommentPermissions(comment);
  const { success } = useToast();
  const { toggleBookmark } = useBookmarkComment({ comment });
  const editModal = useEditModal();
  const reportModal = useReportModal();
  const deleteModal = useDeleteModal();

  const handleCommentDeleted = (deleted: Comment) => {
    onCommentDeleted(deleted);
    if (deepLinkedCommentId && isTopLevelComment(deleted)) showAllComments();
  };

  return [
    canBookmark && {
      label: comment.bookmark_id
        ? t("comments.action_menu.bookmark.remove")
        : t("comments.action_menu.bookmark.add"),
      disabled: isReadOnly,
      onClick: toggleBookmark,
    },
    canReport &&
      !isFlagged(comment) && {
        label: t("comments.action_menu.report.label"),
        disabled: isReadOnly,
        onClick: () => void reportModal.show({ comment }),
      },
    canEdit && {
      label: t("comments.action_menu.edit.label"),
      disabled: isReadOnly,
      onClick: () => void editModal.show({ comment, spaceId }),
    },
    {
      label: t("comments.action_menu.copy_url.label"),
      onClick: () => {
        copyToClipboard(getCommentUrl(comment.id));
        success(t("comments.action_menu.copy_url.url_copied"));
      },
    },
    canDestroy && {
      label: t("comments.action_menu.delete.label"),
      danger: true,
      disabled: isReadOnly,
      onClick: () =>
        void deleteModal.show({
          comment,
          onCommentDeleted: handleCommentDeleted,
        }),
    },
  ].filter(isTruthy);
};
